/**
 * PublicShortlistView — unauthenticated hiring-manager view of a shortlist.
 *
 * Renders a clean, printable list of candidate cards with match score,
 * current title/company, current + desired CTC, and skills. Contact info
 * (email/phone) is stripped by the backend for public shares.
 *
 * Route: /shortlist/:token — no auth guard, gated by the share_token +
 * ``is_public`` flag on the backend.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Award, ExternalLink, Loader2, Printer } from "lucide-react";
import { formatCompCurrency } from "@/lib/budget";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const num = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
};
const pickComp = (c) => {
    const sp = c?.screening_profile || {};
    return {
        currency: sp.ctc_currency || c?.salary_currency || "INR",
        current: num(sp.current_ctc_annual) ?? num(c?.current_salary),
        desired: num(sp.desired_ctc_annual) ?? num(c?.desired_salary_max) ?? num(c?.desired_salary_min),
    };
};

export default function PublicShortlistView() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${BACKEND}/api/public/shortlists/${token}`);
                setData(res.data);
            } catch (err) {
                setError(err?.response?.data?.detail || "Shortlist not available.");
            }
        })();
    }, [token]);

    if (error) return (
        <div className="min-h-screen grid place-items-center bg-background p-6" data-testid="public-shortlist-error">
            <div className="border border-border bg-white p-8 max-w-lg text-center">
                <h1 className="font-display font-black text-2xl">Shortlist not available</h1>
                <p className="text-sm text-muted-foreground mt-2">{error}</p>
                <p className="text-xs text-muted-foreground mt-4">
                    The share link may have been revoked or made private.
                </p>
            </div>
        </div>
    );
    if (!data) return (
        <div className="min-h-screen grid place-items-center bg-background">
            <Loader2 className="w-6 h-6 animate-spin text-ikb" />
        </div>
    );

    const sl = data.shortlist || {};
    const cands = data.candidates || [];
    const apps = data.applications || [];
    const appByCand = Object.fromEntries(apps.map((a) => [a.candidate_id, a]));

    return (
        <div className="min-h-screen bg-background pb-16" data-testid="public-shortlist">
            {/* Header */}
            <div className="bg-white border-b border-border">
                <div className="max-w-5xl mx-auto px-4 py-6 flex items-start gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-ikb">Candidate shortlist · shared preview</div>
                        <h1 className="font-display font-black text-3xl sm:text-4xl mt-1 leading-tight">{sl.name}</h1>
                        {sl.description && <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{sl.description}</p>}
                        <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                            {sl.count || cands.length} candidate{(sl.count || cands.length) === 1 ? "" : "s"}
                            {sl.created_by_name && <> · Curated by {sl.created_by_name}</>}
                            {data.job?.title && <> · For {data.job.title}</>}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => window.print()}
                        className="h-9 px-3 border border-border text-[11px] font-mono uppercase tracking-widest hover:border-ikb hover:text-ikb inline-flex items-center gap-1.5"
                        data-testid="public-shortlist-print"
                    >
                        <Printer className="w-3.5 h-3.5" /> Print / PDF
                    </button>
                </div>
            </div>

            {/* Cards */}
            <div className="max-w-5xl mx-auto px-4 py-6 space-y-3">
                {cands.length === 0 && (
                    <div className="text-center text-muted-foreground text-sm py-12">No candidates linked to this shortlist.</div>
                )}
                {cands.map((c, i) => {
                    const app = appByCand[c.id] || {};
                    const comp = pickComp(c);
                    return (
                        <div key={c.id} className="border border-border bg-white p-4 flex gap-4 items-start" data-testid={`public-shortlist-card-${i}`}>
                            <div className="w-12 h-12 bg-ikb text-white grid place-items-center font-display font-black text-lg shrink-0">
                                {(c.name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <div className="font-semibold">{c.name || "Candidate"}</div>
                                    {app.match_score != null && (
                                        <span className={`text-[10px] font-mono px-1.5 py-0.5 border ${app.match_score >= 75 ? "border-emerald-500 bg-emerald-50 text-emerald-800" : app.match_score >= 50 ? "border-amber-500 bg-amber-50 text-amber-800" : "border-rose-400 bg-rose-50 text-rose-800"}`}>
                                            <Award className="w-3 h-3 inline mr-0.5" /> {app.match_score}
                                        </span>
                                    )}
                                    {app.stage && (
                                        <span className="text-[10px] font-mono px-1.5 py-0.5 border border-border">{app.stage}</span>
                                    )}
                                </div>
                                <div className="text-xs text-muted-foreground mt-0.5">
                                    {c.current_title || "—"}
                                    {c.current_company && <> · {c.current_company}</>}
                                    {c.location && <> · {c.location}</>}
                                    {c.total_experience_years != null && <> · {c.total_experience_years} yrs</>}
                                </div>
                                <div className="mt-2 flex flex-wrap gap-3 text-xs">
                                    {comp.current != null && (
                                        <span className="font-mono">
                                            <span className="text-muted-foreground text-[10px] uppercase tracking-widest mr-1">Current</span>
                                            {formatCompCurrency(comp.current, comp.currency)}
                                        </span>
                                    )}
                                    {comp.desired != null && (
                                        <span className="font-mono">
                                            <span className="text-muted-foreground text-[10px] uppercase tracking-widest mr-1">Desired</span>
                                            {formatCompCurrency(comp.desired, comp.currency)}
                                        </span>
                                    )}
                                </div>
                                {(c.skills || []).length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1">
                                        {(c.skills || []).slice(0, 10).map((s) => (
                                            <span key={s} className="text-[10px] font-mono border border-border px-1.5 py-0.5">{s}</span>
                                        ))}
                                    </div>
                                )}
                                {app.ai_summary && (
                                    <p className="mt-2 text-xs text-foreground/80 leading-relaxed line-clamp-3">{app.ai_summary}</p>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="max-w-5xl mx-auto px-4 text-[10px] font-mono uppercase tracking-widest text-muted-foreground text-center">
                Powered by Portalwiz — <a href="/" className="text-ikb hover:underline">portalwiz.ai</a>
            </div>
        </div>
    );
}
